import { Injectable } from '@angular/core';
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IAccount } from '../models/Account';
import { LocationService } from './location.service';
import { SessionManagerService } from './session-manager.service';
import { UserIdleService } from './user-idle.service';

export function getSettings(): UserManagerSettings {
  return {
    authority: environment.auth.authority,
    client_id: environment.auth.clientId,
    redirect_uri: `${window.location.origin}/signincallback`,
    silent_redirect_uri: `${window.location.origin}/silentcallback`,
    response_type: 'code',
    scope: environment.auth.scope,
    loadUserInfo: false,
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _userManager = new UserManager(getSettings());
  private _user: User | null = null;
  private readonly _loginChangedSubject = new Subject<boolean>();
  private readonly _userLoadedSubject = new Subject<User | null>();
  private readonly _userUnloadedSubject = new Subject<void>();
  private readonly _accessTokenExpired = new Subject<User | null>();
  private readonly _accountSubject = new BehaviorSubject<IAccount | null>(null);

  loginChanged = this._loginChangedSubject.asObservable();
  userLoaded = this._userLoadedSubject.asObservable();
  userUnloaded = this._userUnloadedSubject.asObservable();
  accessTokenExpired = this._accessTokenExpired.asObservable();
  account$ = this._accountSubject.asObservable();

  constructor(
    private readonly sessionManagerService: SessionManagerService,
    private readonly location: LocationService,
    private readonly userIdleService: UserIdleService
  ) {
    this._userManager.events.addUserUnloaded(() => this._userUnloadedSubject.next());
    this._userManager.events.addUserLoaded((u) => {
      this._userLoadedSubject.next(u);
    });
    this._userManager.events.addAccessTokenExpired(() => {
      this._accessTokenExpired.next(null);
    });

    window.addEventListener('focus', () => {
      this.validateUserSession();
    });

    this.userIdleService.idle$.subscribe((isIdle) => {
      if (isIdle) {
        this.resetUser();
        location.reload();
      }
    });
  }

  async login(): Promise<any> {
    return await this._userManager.signinRedirect({
      extraQueryParams: {
        prompt: 'none',
      },
    });
  }

  logout() {
    return this._userManager.signoutRedirectCallback();
  }

  async isLoggedIn(): Promise<boolean> {
    const user = await this._userManager.getUser();
    const userCurrent = !!user && !user.expired;
    if (this._user !== user) {
      this._loginChangedSubject.next(userCurrent);
    }
    this._user = user;
    return userCurrent;
  }

  async getUser(): Promise<User | null> {
    return await this._userManager.getUser();
  }

  async loginComplete() {
    this.sessionManagerService.clear();
    const user = await this._userManager.signinRedirectCallback();
    this._user = user;
    this._accountSubject.next(user.profile as any);
    this._loginChangedSubject.next(!!user && !user.expired);
    return user;
  }

  silentLoginCallback() {
    return this._userManager.signinSilentCallback();
  }

  /**
   * If we believe there's an active session, validate it against OP to make
   * sure the user is still logged in.
   */
  async validateUserSession() {
    if (await this.isLoggedIn()) {
      let valid = false;
      try {
        const user = await this._userManager.signinSilent();
        if (user) valid = true;
      } catch {
        /* empty */
      }
      if (!valid) {
        this.resetUser();
        this.location.reload();
      }
    }
  }

  async getAccessToken() {
    let currentUser;
    const user = await this.getUser();
    currentUser = user;
    return currentUser?.access_token;
  }

  getAccount() {
    this.getUser()
      .then((user) => {
        if (user?.profile) {
          this._accountSubject.next(user.profile as any);
        }
      })
      .catch(() => {});
    return this._accountSubject.value;
  }

  resetUser() {
    this._userManager.removeUser();
    this.sessionManagerService.clear();
    localStorage.clear();
  }
}
